import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import styled from "styled-components";
import Divider from "src/components/Divider";

function SignModalContent(props: {
  setSignBase64: React.Dispatch<React.SetStateAction<string | null>>;
  name: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  setModalState?: React.Dispatch<React.SetStateAction<boolean>>;
  rejectConfirmCondition?: boolean;
  rejectConfirmMsg?: string;
}) {
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    props.setSignBase64(null);
    setHasSignature(false);
  }, []);

  const ref = useRef<SignatureCanvas>(null);

  const handleRefresh = () => {
    props.setSignBase64(null);
    setHasSignature(false);
    ref.current?.clear();
  };

  const handleCancel = () => {
    props.onCancel && props.onCancel();
    props.setModalState && props.setModalState(false);
  };

  const handleConfirm = async () => {
    if (props.rejectConfirmCondition) {
      alert(props.rejectConfirmMsg || "먼저 서명을 진행하세요.");
      return;
    }
    if (props.onConfirm) {
      await props.onConfirm();
    }
    props.setModalState && props.setModalState(false);
  };

  return (
    <SignatureModalContainer>
      <SignatureModal>
        <SignatureModalContent>
          <SignatureHeader>
            <SignatureTitle>신청인 서명</SignatureTitle>
          </SignatureHeader>

          <Divider />

          <SignatureFormSection>
            <SignatureForm>
              <SignatureFormContent>
                <ApplicantInfo>신청인: {props.name}</ApplicantInfo>
                <SignatureArea>
                  <SignatureCanvas
                    penColor="black"
                    backgroundColor="transparent"
                    ref={ref}
                    canvasProps={{
                      width: 250,
                      height: 150,
                      className: "sigCanvas",
                    }}
                    onEnd={(e) => {
                      const dd = ref?.current?.toDataURL();
                      props.setSignBase64(dd as string);
                      setHasSignature(true);
                    }}
                  />
                </SignatureArea>
              </SignatureFormContent>
              <SignatureFormActions>
                <ErrorMessage>반드시 서명해주세요.</ErrorMessage>
                <RefreshButton onClick={handleRefresh}>
                  <RefreshIcon>
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 10 10"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 9.66658C3.69722 9.66658 2.59375 9.2145 1.68958 8.31034C0.785417 7.40617 0.333334 6.3027 0.333334 4.99992C0.333334 3.69714 0.785417 2.59367 1.68958 1.6895C2.59375 0.785335 3.69722 0.333252 5 0.333252C5.67083 0.333252 6.3125 0.471794 6.925 0.748877C7.5375 1.02596 8.0625 1.42214 8.5 1.93742V0.333252H9.66667V4.41658H5.58333V3.24992H8.03333C7.72222 2.70547 7.29688 2.2777 6.75729 1.96659C6.21771 1.65547 5.63195 1.49992 5 1.49992C4.02778 1.49992 3.20139 1.8402 2.52083 2.52075C1.84028 3.20131 1.5 4.0277 1.5 4.99992C1.5 5.97214 1.84028 6.79853 2.52083 7.47908C3.20139 8.15964 4.02778 8.49992 5 8.49992C5.74861 8.49992 6.42431 8.28603 7.02708 7.85825C7.62986 7.43047 8.05278 6.86659 8.29583 6.16658H9.52083C9.24861 7.19714 8.69444 8.03811 7.85833 8.6895C7.02222 9.34089 6.06945 9.66658 5 9.66658Z"
                        fill="#1A1A1A"
                      />
                    </svg>
                  </RefreshIcon>
                  <RefreshText>다시쓰기</RefreshText>
                </RefreshButton>
              </SignatureFormActions>
            </SignatureForm>
          </SignatureFormSection>
        </SignatureModalContent>
        <SignatureModalButtons>
          <SignatureCancelButton onClick={handleCancel}>뒤로</SignatureCancelButton>
          <SignatureConfirmButton onClick={handleConfirm} disabled={!hasSignature}>
            서명하기
          </SignatureConfirmButton>
        </SignatureModalButtons>
      </SignatureModal>
    </SignatureModalContainer>
  );
}

export default SignModalContent;

const SignatureModalContainer = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 11000001;
`;

const SignatureModal = styled.div`
  display: flex;
  flex-direction: column;
  width: 90%;
  max-width: 400px;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const SignatureModalContent = styled.div`
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SignatureHeader = styled.div`
  padding: 0 24px;
`;

const SignatureTitle = styled.h2`
  color: #1a1a1a;
  font-family: Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  margin: 0;
`;

const SignatureFormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0 24px 24px 24px;
`;

const SignatureForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SignatureFormContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ApplicantInfo = styled.div`
  color: #1a1a1a;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
`;

const SignatureArea = styled.div`
  width: 100%;
  height: 150px;
  background: #f0f0f4;
  border: 1px solid #d1d5d8;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  .sigCanvas {
    width: 100% !important;
    height: 100% !important;
  }
`;

const SignatureFormActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 12px;
`;

const ErrorMessage = styled.div`
  color: #ff0000;
  font-family: Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #f0f0f4;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: Pretendard, sans-serif;

  &:hover {
    background: #e0e0e4;
  }
`;

const RefreshIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RefreshText = styled.span`
  color: #1a1a1a;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
`;

const SignatureModalButtons = styled.div`
  display: flex;
  height: 60px;
  justify-content: center;
  align-items: flex-start;
  margin-top: auto;
`;

const SignatureCancelButton = styled.button`
  display: flex;
  width: 100%;
  height: 60px;
  padding: 16px 46px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 0 0 0 16px;
  background: var(--teeshot-g1, #79818a);
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;
  color: var(--teeshot-white, #fdfdfd);
  text-align: center;
  font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;

  &:hover {
    background: #6b7580;
  }
`;

const SignatureConfirmButton = styled.button`
  display: flex;
  width: 100%;
  height: 60px;
  padding: 16px 46px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 0 0 16px 0;
  background: var(--teeshot-green, #004b49);
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;
  color: var(--teeshot-white, #fdfdfd);
  text-align: center;
  font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;

  &:hover {
    background: #003a38;
  }

  &:disabled {
    background: #d1d5d8;
    color: #79818a;
    cursor: not-allowed;
  }
`;
